import { User } from '@/features/admin/types/user';
import { Client } from '@/features/clients';
import {
  CreateUpdateClientUserFormSchema,
  CreateUpdateClientUserWithGeoFormSchema
} from '@/features/clients/models/create-update-client-user-form-schema';
import { SubscriptionFormInputs } from '@/features/clients/models/subscription-form-inputs';
import { artiaProtectedApi } from '@/lib/api-client';
import { deleteWithValidation, postWithValidation, putWithValidation } from '@/lib/api-helpers';

import { ClientFormInputs } from '../models/client-form-inputs';

type ClientRequest = {
  name: string
  assignedAnalysts: number[],
  trackedClinicalsProducts?: number[],
  trackedClassifications?: number[]
};

export const useClientService = () => {
  const getClients = (): Promise<Client[]> => {
    return artiaProtectedApi.get('clients');
  };

  const getClient = (id: number): Promise<Client> => {
    return artiaProtectedApi.get(`clients/${id}`);
  };

  const getClientUsers = (id: number): Promise<User[]> => {
    return artiaProtectedApi.get(`clients/${id}/users`);
  };

  const getClientUser = (clientId: number, userId: number): Promise<User> => {
    return artiaProtectedApi.get(`clients/${clientId}/users/${userId}`);
  };

  const createClientRequest = ({ name, assignedAnalysts }: ClientFormInputs): ClientRequest => {
    return {
      name,
      assignedAnalysts: assignedAnalysts.map(analyst => analyst.id)
    };
  };

  const createClient = (formInputs: ClientFormInputs): Promise<any> => {
    return postWithValidation('clients', { data: createClientRequest(formInputs) });
  };

  const updateClient = (id: number | string, formInputs: ClientFormInputs): Promise<any> => {
    return putWithValidation(`clients/${id}`, { data: createClientRequest(formInputs) });
  };

  const deleteClient = (id: number): Promise<void> => {
    return deleteWithValidation(`clients/${id}`);
  };

  const updateSubscription = (id: number, formInputs: SubscriptionFormInputs): Promise<any> => {
    return putWithValidation(`clients/${id}/subscription`, {
      data: {
        isActive: formInputs.isActive,
        numberOfUsers: formInputs.numberOfUsers ? parseInt(formInputs.numberOfUsers) : null,
        enabledFeatures: formInputs.premiumFeatures,
        isTrackingAllDrugs: formInputs.isTrackingAllDrugs,
        trackedDrugs: formInputs.trackedDrugs?.map(drug => drug.id) ?? [],
        trackedClassifications: formInputs.trackedClassifications.map(classification => classification.id)
      }
    });
  };

  const createClientUser = (clientId: number, formInputs: CreateUpdateClientUserWithGeoFormSchema | CreateUpdateClientUserFormSchema): Promise<{ userId: number }> => {
    return postWithValidation(`clients/${clientId}/users`, {
      data: {
        'email': formInputs.email,
        'name': formInputs.name,
        'enabledFeatures': [...formInputs.enabledFeaturesCapsule, ...formInputs.enabledFeaturesMobile],
        // @ts-ignore
        'zipCode': formInputs.zipCode,
        // @ts-ignore
        'city': formInputs.city,
        // @ts-ignore
        'stateId': formInputs.stateId
      }
    });
  };

  const deleteClientUser = (clientId: number, userId: number): Promise<void> => {
    return deleteWithValidation(`clients/${clientId}/users/${userId}`);
  };

  const updateClientUser = (clientId: number, userId: number, formInputs: CreateUpdateClientUserWithGeoFormSchema | CreateUpdateClientUserFormSchema): Promise<void> => {
    return putWithValidation(`clients/${clientId}/users/${userId}`, {
      data: {
        'email': formInputs.email,
        'name': formInputs.name,
        'enabledFeatures': [...formInputs.enabledFeaturesCapsule, ...formInputs.enabledFeaturesMobile],
        // @ts-ignore
        'zipCode': formInputs.zipCode,
        // @ts-ignore
        'city': formInputs.city,
        // @ts-ignore
        'stateId': formInputs.stateId
      }
    });
  };

  const updateClientPrimaryAdmin = (clientId: number, userId: number): Promise<void> => {
    return putWithValidation(`clients/${clientId}/primary-admin`, {
      data: {
        userId
      }
    });
  };

  const resetClientUserPassword = (clientId: number, userId: number): Promise<void> => {
    return postWithValidation(`clients/${clientId}/users/${userId}/reset-password`, {
      data: {
        userId
      }
    });
  };

  return {
    deleteClient,
    getClients,
    getClient,
    getClientUsers,
    getClientUser,
    createClient,
    updateClient,
    deleteClientUser,
    updateSubscription,
    createClientUser,
    updateClientUser,
    updateClientPrimaryAdmin,
    resetClientUserPassword
  };
};
